/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { DateTime } from 'luxon';
import { RegistryAnalyticsInterval } from './analytics-types';
import { INTERVALS, RangeSelection } from './analytics-period';
import { fieldsOf, formatBound } from './date-expressions';

// The buckets a series is cut into. A bucket is named by its start: a day ("2026-07-21") or,
// below a day, a date-time, which the API spells "2026-07-21T06:00" and the charts
// "2026-07-21 06:00". Everything here reads either spelling.

/** The start of a bucket, from either spelling of its label. */
export function bucketMoment(label: string): DateTime {
  return DateTime.fromISO(label.replace(' ', 'T'), { zone: 'utc' });
}

/** Chart-facing bucket label: sub-day buckets carry a time of day ("2026-07-21 06:00"). */
export function displayBucket(t: string): string {
  return t.replace('T', ' ');
}

/** The end (exclusive) of the bucket starting at `start`: a calendar month, or the interval's hours. */
export function bucketEnd(start: DateTime, interval: RegistryAnalyticsInterval): DateTime {
  return interval === 'month' ? start.plus({ months: 1 }) : start.plus({ hours: INTERVALS[interval].hours });
}

/**
 * How many leading buckets have ended by now, each judged by its own end (see {@link bucketEnd}).
 * The charts keep the rest on the axis but plot nothing there, as the window's empty end:
 * plotted, a still-filling or future bucket reads as a collapse.
 */
export function finishedBuckets(
  labels: string[],
  interval: RegistryAnalyticsInterval,
  now: number = Date.now()
): number {
  let count = labels.length;
  while (count > 0 && bucketEnd(bucketMoment(labels[count - 1]), interval).toMillis() > now) {
    count--;
  }
  return count;
}

/** A series up to its last finished bucket (see {@link finishedBuckets}). */
export function finishedPoints<T extends { t: string }>(
  points: T[],
  interval: RegistryAnalyticsInterval,
  now: number = Date.now()
): T[] {
  return points.slice(
    0,
    finishedBuckets(
      points.map((point) => point.t),
      interval,
      now
    )
  );
}

/** The bucket label a date falls into: the greatest label <= date, or undefined before the range. */
export function bucketLabelFor(date: string, labels: string[]): string | undefined {
  let match: string | undefined;
  for (const label of labels) {
    // labels may carry a time of day (sub-day buckets); compare by day
    const day = label.slice(0, 10);
    if (day < date) {
      match = label;
    } else if (day === date) {
      // the release day's first bucket
      return label;
    } else {
      break;
    }
  }
  return match;
}

const DAY_AND_MONTH = { day: 'numeric', month: 'short' } as const;

/**
 * The period each bucket covers, for readouts of a hovered point: "8 Jul 2026, 06:00–12:00 UTC"
 * for sub-day buckets (cut in UTC), "8 Jul 2026" for a day, "8 Jul – 14 Jul 2026" across days
 * and "Jul 2026" for a whole month.
 */
export function bucketPeriodLabels(points: { t: string }[], interval: RegistryAnalyticsInterval): string[] {
  const hours = INTERVALS[interval].hours;
  const date = (moment: DateTime) => moment.toLocaleString({ ...DAY_AND_MONTH, year: 'numeric' });
  const time = (moment: DateTime) => moment.toLocaleString(DateTime.TIME_24_SIMPLE);
  return points.map((point, index) => {
    const start = bucketMoment(point.t);
    // the next bucket's start, or this one's own end for the last
    const next = points[index + 1] ? bucketMoment(points[index + 1].t) : bucketEnd(start, interval);
    if (hours < 24) {
      return `${date(start)}, ${hours === 1 ? time(start) : `${time(start)}–${time(next)}`} UTC`;
    }
    const end = next.minus({ milliseconds: 1 });
    if (start.hasSame(end, 'day')) {
      return date(start);
    }
    if (start.day === 1 && next.day === 1 && end.hasSame(start, 'month')) {
      return start.toLocaleString({ month: 'short', year: 'numeric' });
    }
    return `${start.toLocaleString(DAY_AND_MONTH)} – ${date(end)}`;
  });
}

/**
 * The custom selection covering a run of chart buckets, first to last inclusive: a drag across
 * a chart. An end past now becomes "now", so a drag to the window's edge stays live; undefined
 * when the run starts in the future.
 */
export function bucketSelection(
  first: string,
  last: string,
  interval: RegistryAnalyticsInterval,
  now: DateTime = DateTime.utc()
): RangeSelection | undefined {
  const start = bucketMoment(first);
  const end = bucketEnd(bucketMoment(last), interval);
  if (start >= now) {
    return undefined;
  }
  return {
    range: 'custom',
    ...fieldsOf({ from: formatBound(start), to: end >= now ? 'now' : formatBound(end) })
  };
}
